//LIBRARIES
import React, { Fragment } from 'react';
import { FieldArray } from 'formik';
import styled from 'styled-components';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';

//HELPERS
import { getTranslatedString } from 'helpers/i18n';

//COMPONENTS
import Spacer from 'components/ui/Spacer';
import TextBox from 'components/ui/TextBox';
import Form from 'components/ui/Form';
import Row from 'components/ui/Row';
import Col from 'components/ui/Col';
import Select from 'components/ui/SelectField';
import CtaCard from 'components/ui/CtaCard';
import ReactPhoneInput from 'components/shared/ReactPhoneInput';

const Title = styled.p`
  font-size: 18px;
  font-weight: bold;
  margin-top: 25px;
`;

const AddOrRemoveButton = styled(CtaCard)`
  padding: 0;
  float: ${(props) => props.align || 'right'};
  margin-bottom: 30px;
`;

const emergencyContactType = 'landlordPortal.emergencyContactsFieldArray';

const ActionButtons = (index, lengthOfArray, arrayHelpers) => {
  if (!isEqual(index, lengthOfArray - 1)) {
    return (
      <Spacer>
        <Row>
          <Col span={8} offset={16}>
            <AddOrRemoveButton type="button" onClick={() => arrayHelpers.remove(index)}>
              {getTranslatedString('removeEmergencyContact')}
            </AddOrRemoveButton>
          </Col>
        </Row>
      </Spacer>
    );
  }

  return (
    <Spacer>
      <Row>
        <Col span={8}>
          <AddOrRemoveButton
            align="left"
            type="button"
            onClick={() => arrayHelpers.push(initialFormValue)}
          >
            {getTranslatedString('addEmergencyContact')}
          </AddOrRemoveButton>
        </Col>
        <Col span={8} offset={8}>
          <AddOrRemoveButton type="button" onClick={() => arrayHelpers.remove(index)}>
            {getTranslatedString('removeEmergencyContact')}
          </AddOrRemoveButton>
        </Col>
      </Row>
    </Spacer>
  );
};

const initialFormValue = {
  full_name: '',
  phone_number: '',
  relationship: 'Friend',
};

const EmergencyContact = ({ values, errors, setFieldValue }) => {
  const hasEmergencyContacts = !isEmpty(values.emergency_contacts);
  const handleChange = (name, newVal, index) => {
    setFieldValue(`emergency_contacts.${index}.${name}`, newVal);
  };
  return (
    <Fragment>
      <Spacer mb={1}>
        <Title>{getTranslatedString('emergencyContact')}</Title>
        <FieldArray
          name="emergency_contacts"
          render={(arrayHelpers) => {
            if (!hasEmergencyContacts) {
              return (
                <AddOrRemoveButton
                  align="left"
                  type="button"
                  onClick={() => arrayHelpers.push(initialFormValue)}
                >
                  {getTranslatedString('addEmergencyContact')}
                </AddOrRemoveButton>
              );
            }
            return (
              <Fragment>
                {values.emergency_contacts.map((emergency_contact, index) => {
                  const errorCheck =
                    errors['emergency_contacts'] && errors['emergency_contacts'][index];

                  return (
                    <div key={index}>
                      <Spacer mb={1}>
                        <Row gutter={16}>
                          <Col span={12}>
                            <Form.Item
                              label={`${getTranslatedString(`${emergencyContactType}.fullName`)}*`}
                              validateStatus={errorCheck?.full_name && 'error'}
                              help={errorCheck?.full_name}
                            >
                              <TextBox
                                name="full_name"
                                value={emergency_contact.full_name}
                                onChange={(e) => handleChange('full_name', e.target.value, index)}
                                placeholder="e.g. John Doe"
                                size="large"
                              />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item
                              label={`${getTranslatedString(
                                `${emergencyContactType}.phoneNumber`
                              )}*`}
                              validateStatus={errorCheck?.phone_number && 'error'}
                              help={errorCheck?.phone_number}
                            >
                              <ReactPhoneInput
                                name="phone_number"
                                value={emergency_contact.phone_number}
                                onChange={(val) => handleChange('phone_number', val, index)}
                                hasError={errorCheck?.phone_number}
                              />
                            </Form.Item>
                          </Col>
                        </Row>
                      </Spacer>
                      <Spacer mb={1}>
                        <Row gutter={16}>
                          <Col span={12}>
                            <Form.Item
                              label={`${getTranslatedString(`${emergencyContactType}.email`)}`}
                              validateStatus={errorCheck?.email && 'error'}
                              help={errorCheck?.email}
                            >
                              <TextBox
                                name="email"
                                value={emergency_contact.email}
                                onChange={(e) => handleChange('email', e.target.value, index)}
                                placeholder="e.g. johndoe@gmail.com"
                                size="large"
                              />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item
                              label={`${getTranslatedString(
                                `${emergencyContactType}.relationshipType`
                              )}*`}
                              validateStatus={errorCheck?.relationship && 'error'}
                              help={errorCheck?.relationship}
                            >
                              <Select
                                name="relationship"
                                onChange={(val) => handleChange('relationship', val, index)}
                                value={emergency_contact.relationship}
                                size="large"
                              >
                                <Select.Option value="Friend">{`${getTranslatedString(
                                  `${emergencyContactType}.friend`
                                )}`}</Select.Option>
                                <Select.Option value="Family">{`${getTranslatedString(
                                  `${emergencyContactType}.family`
                                )}`}</Select.Option>
                                <Select.Option value="Significant Other">{`${getTranslatedString(
                                  `${emergencyContactType}.significantOther`
                                )}`}</Select.Option>
                                <Select.Option value="Work Associate">{`${getTranslatedString(
                                  `${emergencyContactType}.workAssociate`
                                )}`}</Select.Option>
                              </Select>
                            </Form.Item>
                          </Col>
                        </Row>
                      </Spacer>
                      {ActionButtons(index, values.emergency_contacts.length, arrayHelpers)}
                    </div>
                  );
                })}
              </Fragment>
            );
          }}
        />
      </Spacer>
    </Fragment>
  );
};

export default EmergencyContact;
